import { Button, TableItem } from 'components/atoms';
import LabeledDeliveryStatus from 'components/molecules/labeledStatus/LabeledDeliveryStatus';
import { Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { useTableColumnsVisibility } from 'hooks/useTableColumnsVisibility';
import { resources } from 'locales/i18n';
import { useMemo } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { PiMagicWand, PiWarningOctagon } from 'react-icons/pi';
import { DELIVERY_STATUS } from 'services/music-product-digital/types/enums';
import { Column } from 'types/components/table/table.types';

enum SORT_COLUMNS {
  value,
  name,
  label,
  en,
  pl,
}

const DeliveryStatus = () => {
  const { t } = useTranslation();

  const columnsVisibility = useTableColumnsVisibility(
    'DeliveryStatusDevTools',
    SORT_COLUMNS
  );

  const appLanguages = useMemo<string[]>(
    () => Object.keys(resources),
    [resources]
  );
  const deliveryStatuses = Object.entries(DELIVERY_STATUS)
    .map(([key, value]) => ({
      id: `${value}`,
      value,
      label: DELIVERY_STATUS[value as DELIVERY_STATUS],
    }))
    .splice(
      Object.entries(DELIVERY_STATUS).length * 0.5 + 1,
      Object.entries(DELIVERY_STATUS).length
    );

  const onClickToCopy = (title: string, value: any) => {
    toast.success(`${title} ${value} has been copied to clipboard!`);
    navigator.clipboard.writeText(value);
  };

  const getTranslation = (lng: string, id: string) =>
    t(`enum.DELIVERY_STATUS.${id}`, { lng: lng });

  const getMissingLanguages = () => {
    const missingLanguagesByLanguage = appLanguages.map(l => ({
      [l]: deliveryStatuses
        .filter(e =>
          getTranslation(l, `${e.label}`).includes('enum.DELIVERY_STATUS.')
        )
        .map(e => e.label),
    }));

    console.log(
      'Missing delivery status translations:',
      missingLanguagesByLanguage
    );
    toast.success(`Check the console for result`);
  };

  return (
    <TopbarPage
      topbar={
        <TopBar
          title={`Delivery status list (${deliveryStatuses?.length ?? 0})`}
          hideBackButton
          topbarContent={
            <>
              <Button isClear onlyIcon onClick={getMissingLanguages}>
                <Button.Description description={t('button.generate')} />

                <Button.Icon icon={<PiMagicWand />} />
              </Button>
              <Table.CustomColumnsButton
                columns={SORT_COLUMNS}
                isClear
                visibility={columnsVisibility}
                i18nextPrefix=' '
                customI18next=' '
              />
            </>
          }
        />
      }>
      <Table
        id={columnsVisibility.tableId}
        size='compact'
        data={{
          nodes: deliveryStatuses,
        }}
        columns={[
          {
            label: 'Value',
            renderCell: ({ value }) => (
              <TableItem
                className='cursor-pointer'
                onClick={() => onClickToCopy('value', value)}>
                <TableItem.Text text={`${value}`} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(SORT_COLUMNS.value),
          },
          {
            label: 'Name',
            renderCell: ({ label }) => (
              <TableItem
                className='cursor-pointer'
                onClick={() => onClickToCopy('name', label)}>
                <TableItem.Text text={`${label}`} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(SORT_COLUMNS.name),
          },
          {
            label: 'Label',
            renderCell: ({ value }) => (
              <TableItem>
                <LabeledDeliveryStatus status={value as DELIVERY_STATUS} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(SORT_COLUMNS.label),
          },
          ...appLanguages.map<Column<{ id: string; label: string }>>(
            (l, key) => ({
              label: l.toUpperCase(),
              renderCell: ({ label }) => (
                <TableItem>
                  {getTranslation(l, label).includes(
                    'enum.DELIVERY_STATUS.'
                  ) ? (
                    <TableItem.Label color='red' size='compact'>
                      <PiWarningOctagon /> NOT FOUND
                    </TableItem.Label>
                  ) : (
                    <TableItem.Text text={getTranslation(l, label)} />
                  )}
                </TableItem>
              ),
              resize: key !== appLanguages.length - 1,
              hide: columnsVisibility.hide(
                SORT_COLUMNS[l as keyof typeof SORT_COLUMNS]
              ),
            })
          ),
        ]}
      />
    </TopbarPage>
  );
};

export default DeliveryStatus;
